/**
 * Prompt-Eingabemaske (Platzhalter-Prompts mit eigenen Angaben) — Handy-App-Pendant
 * zur Web-Funktion `oeffnePromptDialog` (public/assets/app.js). Öffnet bei Klick auf
 * einen Prompt mit [Platzhaltern]: je Platzhalter ein Feld (Frage aus dem Klammertext),
 * Eingabe per Text ODER Sprache (Recorder wiederverwendet), Live-Vorschau des fertigen
 * Prompts (Substitution, bearbeitbar) → Kopieren oder direkt an die Kurs-KI senden.
 * Signal-gesteuert (state/coach: pendingPromptDialog); einmal in App.tsx gemountet.
 */
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useDialog } from "../ui/useDialog";
import { createRecorder } from "./recorder";
import { askQuestion, pendingPromptDialog } from "../state/coach";
import { showToast } from "../ui/toast";
import { IconMic, IconClose } from "../app/icons";

/** Aus dem Klammertext eine kurze Frage + optionalen Beispiel-Hinweis ableiten. */
function platzhalterFrage(ph: string): { frage: string; beispiel: string } {
  let inner = ph.replace(/^\[+|\]+$/g, "").trim();
  let beispiel = "";
  const m = inner.match(/^(.*?)(?:\s*[—–:]\s*|\s+(?:z\.?\s*B\.?|etwa)\s+)(.+)$/i);
  if (m && m[1].trim()) {
    inner = m[1].trim();
    beispiel = "z. B. " + m[2].trim().replace(/^z\.?\s*B\.?\s*[:.]?\s*/i, "");
  }
  if (/^[A-ZÄÖÜ0-9 _/-]{3,}$/.test(inner)) inner = inner.charAt(0) + inner.slice(1).toLowerCase();
  inner = inner.replace(/\bmein(e|en|em|er|es)?\b/gi, (_m: string, suf?: string) => "dein" + (suf || ""));
  if (!inner) return { frage: "Was möchtest du hier einsetzen?", beispiel: "" };
  const label = inner.charAt(0).toUpperCase() + inner.slice(1);
  return { frage: `${label} — was trägst du ein?`, beispiel };
}

export function PromptDialog() {
  const text = pendingPromptDialog.value;
  if (!text) return null;
  return <PromptDialogInner key={text} text={text} />;
}

function PromptDialogInner({ text }: { text: string }) {
  const phs = useMemo(() => {
    const all = text.match(/\[[^\]]+\]/g) || [];
    const u: string[] = [];
    all.forEach((p) => {
      if (!u.includes(p)) u.push(p);
    });
    return u;
  }, [text]);
  const [vals, setVals] = useState<string[]>(() => phs.map(() => ""));
  const [manual, setManual] = useState<string | null>(null);
  const recIdxRef = useRef<number | null>(null);
  const aktivRef = useRef(0); // zuletzt fokussiertes Feld (Cursor-Feld) -> Ziel der Spracheingabe
  const aliveRef = useRef(true);
  const dialogRef = useRef<HTMLElement>(null);

  function close() {
    pendingPromptDialog.value = null;
  }
  useDialog(dialogRef, close);

  const recorder = useMemo(
    () =>
      createRecorder({
        onText: (t) => {
          if (!aliveRef.current) return; // Dialog schon zu -> spätes Transkript ignorieren
          const i = recIdxRef.current;
          if (i == null) return;
          setVals((v) => {
            const n = [...v];
            n[i] = n[i] ? `${n[i]} ${t}` : t;
            return n;
          });
          setManual(null);
        },
        onNotice: (t) => {
          if (aliveRef.current) showToast(t);
        },
      }),
    [],
  );
  useEffect(
    () => () => {
      aliveRef.current = false;
      recorder.dispose();
    },
    [recorder],
  );

  const fertig =
    manual ??
    phs.reduce((out, ph, i) => (vals[i]?.trim() ? out.split(ph).join(vals[i].trim()) : out), text);

  function toggleMic() {
    const s = recorder.state.value;
    if (s === "transcribing") return; // Transkription läuft -> warten
    if (s === "recording") {
      recorder.toggle(); // laufende Aufnahme stoppen
      return;
    }
    recIdxRef.current = aktivRef.current; // ins zuletzt angetippte Feld (Cursor-Feld) schreiben
    recorder.toggle();
  }

  function copy() {
    void navigator.clipboard?.writeText(fertig).then(() => showToast("Prompt kopiert ✓"));
  }
  function send() {
    const t = fertig;
    close();
    void askQuestion(t);
  }

  const recState = recorder.state.value;

  return (
    <div class="prompt-dialog-backdrop" onClick={close}>
      <section
        ref={dialogRef}
        class="prompt-dialog glass"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pd-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="pd-head">
          <h2 id="pd-title">Prompt für dich anpassen</h2>
          <button type="button" class="pd-close" aria-label="Schließen" onClick={close}>
            <IconClose size={18} />
          </button>
        </div>
        <p class="pd-intro">
          Fülle aus, was die KI von dir wissen soll — der fertige Prompt baut sich automatisch zusammen.
        </p>
        <div class="pd-fields">
          {phs.map((ph, i) => {
            const f = platzhalterFrage(ph);
            return (
              <div class="pd-field" key={ph}>
                <label class="pd-q">{f.frage}</label>
                {f.beispiel ? <p class="pd-hint">{f.beispiel}</p> : null}
                <textarea
                  class="pd-in"
                  rows={2}
                  value={vals[i]}
                  placeholder="Deine Angabe …"
                  onFocus={() => {
                    aktivRef.current = i;
                  }}
                  onInput={(e) => {
                    const val = (e.target as HTMLTextAreaElement).value;
                    setVals((v) => {
                      const n = [...v];
                      n[i] = val;
                      return n;
                    });
                    setManual(null);
                  }}
                />
              </div>
            );
          })}
        </div>
        {/* EIN Mikrofon: füllt das zuletzt angetippte Feld (Cursor-Feld). */}
        <div class="pd-voice">
          <button
            type="button"
            class={`pd-mic${recState === "recording" ? " is-recording" : ""}`}
            aria-label="Per Sprache eingeben"
            title="Per Sprache eingeben"
            disabled={recState === "transcribing"}
            onClick={toggleMic}
          >
            <IconMic size={18} />
          </button>
          <span class="pd-voice-hint">
            {phs.length > 1 ? "Sprechen füllt das zuletzt angetippte Feld" : "Antwort einsprechen"}
          </span>
        </div>
        <div class="pd-preview-wrap">
          <span class="pd-preview-label">
            Fertiger Prompt <small>(bearbeitbar)</small>
          </span>
          <textarea
            class="pd-preview"
            rows={5}
            value={fertig}
            aria-label="Fertiger Prompt"
            onInput={(e) => setManual((e.target as HTMLTextAreaElement).value)}
          />
        </div>
        <div class="pd-actions">
          <button type="button" class="btn-ghost" onClick={copy}>
            Kopieren
          </button>
          <button type="button" class="btn-coach" onClick={send}>
            An die Kurs-KI senden
          </button>
        </div>
      </section>
    </div>
  );
}
